import ks, { kObject, kObjectArray } from "@shared/kalmstromShared4";
import { kEditableTable, kHTMLEditableColType } from "@shared/kEditableTable";
import { kMultiSelectOption, kMultiSelectOptionType } from "@shared/kHTMLMultiSelect";
import { IListDefinition, IFieldDefinition, IDataRow } from "./DBInterfaces";
import { CONFIG } from "./DBConstants";
import DBState from "./DBState";
import DBModalManager from "./DBModalManager";
import DBFieldManager from "./DBFieldManager";
import DBListManager from "./DBListManager";

// ---------------------------------------------------------------------------
// Data row model for kEditableTable
// ---------------------------------------------------------------------------

class DataRow extends kObject {
    constructor() { super(); }
}

class DataRowCollection extends kObjectArray {
    public Items: DataRow[] = [];
}

// ---------------------------------------------------------------------------
// Column descriptor (tracks internalName + kHTMLEditableColType together)
// ---------------------------------------------------------------------------

interface IDataColDef {
    internalName: string;
    fieldDef: IFieldDefinition | null;  // null for the synthetic Title column
    colType: kHTMLEditableColType;
}

// ---------------------------------------------------------------------------

export default class DBDataGrid {

    private static DataGrid: kEditableTable | null = null;
    private static ColDefs: IDataColDef[] = [];

    // -----------------------------------------------------------------------
    // Public API
    // -----------------------------------------------------------------------

    public static OpenDataModal = (listId: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBState.DataModalListId = listId;
            const list: IListDefinition | null = DBState.FindList(listId);
            if (list === null) { return; }
            if (list.Data === null || list.Data === undefined) { list.Data = []; }

            const titleEl: HTMLElement | null = document.getElementById("dataModalTitle");
            if (titleEl !== null) { titleEl.textContent = "Manage Data: " + list.Title; }

            DBDataGrid.BuildGrid(list);
            DBModalManager.OpenModal("dataModal");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBDataGrid.OpenDataModal");
        }
    };

    public static CloseDataModal = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBModalManager.CloseModal("dataModal");
            DBState.DataModalListId = null;
            DBDataGrid.DataGrid = null;
            DBDataGrid.ColDefs = [];
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBDataGrid.CloseDataModal");
        }
    };

    /** Called externally (e.g. after CSV import) to reload the grid from list.Data */
    public static RenderDataGrid = (list: IListDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBDataGrid.DataGrid === null) { return; }
            const collection: DataRowCollection = DBDataGrid.BuildCollection(list);
            DBDataGrid.DataGrid.RowValues = collection;
            DBDataGrid.DataGrid.RemoveContentRows();
            DBDataGrid.DataGrid.AddContentRows();
            DBDataGrid.DataGrid.AddEmptyRows(1);
            DBDataGrid.UpdateDataCount();
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBDataGrid.RenderDataGrid");
        }
    };

    public static AddDataRow = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBDataGrid.DataGrid === null) {
                DBModalManager.ShowToast("Data modal is not open", "error");
                return;
            }
            DBDataGrid.DataGrid.AddEmptyRows(1);
            DBDataGrid.UpdateDataCount();
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBDataGrid.AddDataRow");
        }
    };

    public static ClearAllData = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBDataGrid.DataGrid === null) { return; }
            const listId: string = DBState.DataModalListId || "";
            const list: IListDefinition | null = DBState.FindList(listId);
            if (list === null) { return; }
            const rowCount: number = DBDataGrid.CountNonEmptyRows();
            if (rowCount === 0) {
                DBModalManager.ShowToast("No data to clear", "info");
                return;
            }
            if (confirm('Clear all ' + rowCount + ' data row(s) from "' + list.Title + '"?')) {
                DBDataGrid.DataGrid.RemoveContentRows();
                DBDataGrid.DataGrid.AddEmptyRows(1);
                DBDataGrid.UpdateDataCount();
                DBModalManager.ShowToast("All data cleared", "success");
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBDataGrid.ClearAllData");
        }
    };

    public static PasteData = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBState.SelectedListId === null) {
                DBModalManager.ShowToast("Please select a list first", "error");
                return;
            }
            DBDataGrid.OpenDataModal(DBState.SelectedListId);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBDataGrid.PasteData");
        }
    };

    public static SaveData = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(DBState.DataModalListId || "");
            if (list === null) { return; }
            if (DBDataGrid.DataGrid === null) { return; }

            list.Data = DBDataGrid.ReadGridToDataRows();
            DBState.SaveState();
            DBModalManager.CloseModal("dataModal");
            DBListManager.RenderLists();
            DBModalManager.ShowToast("Data saved: " + list.Data.length + " row(s)", "success");
            DBState.DataModalListId = null;
            DBDataGrid.DataGrid = null;
            DBDataGrid.ColDefs = [];
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBDataGrid.SaveData");
        }
    };

    // -----------------------------------------------------------------------
    // Private: grid construction
    // -----------------------------------------------------------------------

    private static BuildGrid = (list: IListDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const container: HTMLElement | null = document.getElementById("dataGridTableContainer");
            if (container === null) { return; }

            DBDataGrid.DataGrid = new kEditableTable();
            DBDataGrid.DataGrid.AddBlankRows = false;
            DBDataGrid.ColDefs = [];

            // ---- Title column (always first) ----
            const titleColDef: IDataColDef = { internalName: "Title", fieldDef: null, colType: kHTMLEditableColType.Text };
            DBDataGrid.ColDefs.push(titleColDef);
            const titleCol = DBDataGrid.DataGrid.AddColumn("Title", kHTMLEditableColType.Text, 200, "Title", "");
            titleCol.ValidationCB = DBDataGrid.MakeTextValidationCB({ internalName: "Title", displayName: "Title", Type: "string", Format: "", Required: false, MaxLength: 255 });

            // ---- Field columns ----
            const entries: [string, IFieldDefinition][] = Object.entries(list.Properties || {}) as [string, IFieldDefinition][];
            for (const [key, field] of entries) {
                const colType: kHTMLEditableColType = DBDataGrid.MapFieldType(field);
                const width: number = DBDataGrid.GetColWidth(colType);
                const colDef: IDataColDef = { internalName: key, fieldDef: field, colType };
                DBDataGrid.ColDefs.push(colDef);

                const col = DBDataGrid.DataGrid.AddColumn(
                    field.Title || key,
                    colType,
                    width,
                    key,
                    DBDataGrid.GetDefaultValue(field, colType)
                );

                if (colType === kHTMLEditableColType.Select) {
                    col.Options = [...(field.Enum || []), ""];
                } else if (colType === kHTMLEditableColType.SelectMulti) {
                    col.MultiSelectOptions = DBDataGrid.BuildMultiOptions(field);
                }

                col.ValidationCB = DBDataGrid.MakeFieldValidationCB(key, field, colType);
            }

            // ---- Load data ----
            const collection: DataRowCollection = DBDataGrid.BuildCollection(list);
            DBDataGrid.DataGrid.RowValues = collection;

            DBDataGrid.DataGrid.CellDirtiedCB = DBDataGrid.UpdateDataCount;
            DBDataGrid.DataGrid.RowDeletedCB = DBDataGrid.UpdateDataCount;

            container.innerHTML = "";
            container.appendChild(DBDataGrid.DataGrid.ToTable());
            DBDataGrid.DataGrid.AddEmptyRows(1);
            DBDataGrid.UpdateDataCount();
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBDataGrid.BuildGrid");
        }
    };

    private static BuildCollection = (list: IListDefinition): DataRowCollection => {
        const collection: DataRowCollection = new DataRowCollection();
        if (list.Data === null || list.Data === undefined) { return collection; }
        for (const dataRow of list.Data) {
            const row: DataRow = new DataRow();
            for (const colDef of DBDataGrid.ColDefs) {
                const raw: string = (dataRow as any)[colDef.internalName] || "";
                (row as any)[colDef.internalName] = DBDataGrid.ParseValueForGrid(raw, colDef.colType);
            }
            collection.push(row);
        }
        return collection;
    };

    private static ParseValueForGrid = (raw: string, colType: kHTMLEditableColType): any => {
        switch (colType) {
            case kHTMLEditableColType.Checkbox:
                return raw === "true" || raw === "1";
            case kHTMLEditableColType.Number:
                return raw === "" ? 0 : (parseFloat(raw) || 0);
            case kHTMLEditableColType.Currency:
                return raw === "" ? 0 : (parseFloat(raw) || 0);
            default:
                return raw;
        }
    };

    // -----------------------------------------------------------------------
    // Private: read grid back to IDataRow[]
    // -----------------------------------------------------------------------

    private static ReadGridToDataRows = (): IDataRow[] => {
        if (DBDataGrid.DataGrid === null) { return []; }
        const result: IDataRow[] = [];
        for (const gridRow of DBDataGrid.DataGrid.Rows.Items) {
            if (gridRow.IsEmpty()) { continue; }
            const dataRow: IDataRow = {};
            for (const colDef of DBDataGrid.ColDefs) {
                const val: any = gridRow.GetValueFromCell(colDef.internalName);
                (dataRow as any)[colDef.internalName] = DBDataGrid.StringifyValue(val, colDef.colType);
            }
            result.push(dataRow);
        }
        return result;
    };

    private static StringifyValue = (val: any, colType: kHTMLEditableColType): string => {
        if (val === null || val === undefined) { return ""; }
        switch (colType) {
            case kHTMLEditableColType.Checkbox:
                return val === true ? "true" : "false";
            case kHTMLEditableColType.Number:
            case kHTMLEditableColType.Currency:
                return String(val);
            default:
                return String(val || "");
        }
    };

    // -----------------------------------------------------------------------
    // Private: column type mapping helpers
    // -----------------------------------------------------------------------

    private static MapFieldType = (field: IFieldDefinition): kHTMLEditableColType => {
        if (field.Enum !== null && field.Enum !== undefined && field.Enum.length > 0) {
            return kHTMLEditableColType.Select;
        }
        if (field.Type === "array" && field.Items !== null && field.Items !== undefined
                && field.Items.Enum !== null && field.Items.Enum !== undefined && field.Items.Enum.length > 0) {
            return kHTMLEditableColType.SelectMulti;
        }
        if (field.Type === "boolean") { return kHTMLEditableColType.Checkbox; }
        if (field.Type === "integer") { return kHTMLEditableColType.Number; }
        if (field.Type === "number" && field.Format === "currency") { return kHTMLEditableColType.Currency; }
        if (field.Type === "number") { return kHTMLEditableColType.Number; }
        return kHTMLEditableColType.Text;
    };

    private static GetColWidth = (colType: kHTMLEditableColType): number => {
        switch (colType) {
            case kHTMLEditableColType.Checkbox: return 80;
            case kHTMLEditableColType.Number:
            case kHTMLEditableColType.Currency: return 120;
            case kHTMLEditableColType.Select: return 160;
            case kHTMLEditableColType.SelectMulti: return 200;
            default: return 180;
        }
    };

    private static GetDefaultValue = (field: IFieldDefinition, colType: kHTMLEditableColType): any => {
        switch (colType) {
            case kHTMLEditableColType.Checkbox: return false;
            case kHTMLEditableColType.Number:
            case kHTMLEditableColType.Currency: return 0;
            default: return field.DefaultValue || "";
        }
    };

    private static BuildMultiOptions = (field: IFieldDefinition): kMultiSelectOption[] => {
        const opts: kMultiSelectOption[] = [];
        const enumArr: string[] = (field.Items && field.Items.Enum) ? field.Items.Enum : [];
        for (const item of enumArr) {
            const opt: kMultiSelectOption = new kMultiSelectOption();
            opt.Text = item;
            opt.OptionType = kMultiSelectOptionType.Normal;
            opts.push(opt);
        }
        return opts;
    };

    // -----------------------------------------------------------------------
    // Private: validation callbacks
    // -----------------------------------------------------------------------

    private static MakeFieldValidationCB = (key: string, field: IFieldDefinition, colType: kHTMLEditableColType) => {
        return (value: any): string | null => {
            if (colType === kHTMLEditableColType.Checkbox
                    || colType === kHTMLEditableColType.Select
                    || colType === kHTMLEditableColType.SelectMulti) {
                return null;  // no string validation for these
            }
            const strVal: string = value === null || value === undefined ? "" : String(value);
            const col: any = { internalName: key, displayName: field.Title || key, Type: field.Type, Format: field.Format, Required: field.Required, MaxLength: field.MaxLength };
            const result: { valid: boolean; error?: string } = DBFieldManager.ValidateCellValue(strVal, col);
            return result.valid ? null : (result.error || "Invalid value");
        };
    };

    private static MakeTextValidationCB = (colMeta: any) => {
        return (value: any): string | null => {
            const strVal: string = value === null || value === undefined ? "" : String(value);
            const result: { valid: boolean; error?: string } = DBFieldManager.ValidateCellValue(strVal, colMeta);
            return result.valid ? null : (result.error || "Invalid value");
        };
    };

    // -----------------------------------------------------------------------
    // Private: count display
    // -----------------------------------------------------------------------

    private static UpdateDataCount = (): void => {
        try {
            const countEl: HTMLElement | null = document.getElementById("dataCount");
            if (countEl === null) { return; }
            const n: number = DBDataGrid.CountNonEmptyRows();
            countEl.textContent = n + " row" + (n !== 1 ? "s" : "");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBDataGrid.UpdateDataCount");
        }
    };

    private static CountNonEmptyRows = (): number => {
        if (DBDataGrid.DataGrid === null) { return 0; }
        let count: number = 0;
        for (const row of DBDataGrid.DataGrid.Rows.Items) {
            if (!row.IsEmpty()) { count++; }
        }
        return count;
    };
}
